import { ReactNode, useCallback } from 'react';

import DatePicker from 'react-datepicker';

import { Box, Button, colors, FolderStarIcon, Heading2, Heading4, Paragraph, Stack, u } from '@ui';

import { ClubTableModal } from '../ClubTableModal';

import * as css from './ActivityFilter.css';
import { ActivityTagFilter } from './components/ActivityTagFilter';
import { KeywordSearch } from './components/KeywordSearch';
import * as dateCss from './DatePicker.css';
import { useSharedActivityFilter } from './useSharedActivityFilters';

import 'react-datepicker/dist/react-datepicker.css';

export type ActivityFilterProps = {
    totalActivities?: number | null;
    numberOfActivities?: number;
    emptyFilter?: boolean;
    golfers?: ReactNode;
};

export function ActivityFilter({ numberOfActivities, totalActivities, golfers }: ActivityFilterProps) {
    const {
        startDate,
        endDate,
        clubModalOpen,
        selectedClubGroup,
        selectedClubLabel,
        noClubSelected,
        clubCollections,
        filter,
        clearFilter,
        updateFilter,
        setClubModalOpen,
        handleKeywordChange,
        toggleOnlyFavorites,
        openClubModal,
        handleClubSelect,
        handleNoClubSelect,
        handleClubGroupSelect,
        clearClubSelectionUi,
    } = useSharedActivityFilter();

    const selectStartDate = useCallback(
        (date: Date | null) => {
            if (!date) {
                return;
            }
            updateFilter({
                startDate: new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)),
            });
        },
        [updateFilter],
    );

    const selectEndDate = useCallback(
        (date: Date | null) => {
            if (!date) {
                return;
            }
            updateFilter({
                endDate: new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)),
            });
        },
        [updateFilter],
    );

    return (
        <Box
            display="flex"
            position="sticky"
            flexDirection="column"
            height="fit-content"
            className={css.stickyContainer}
        >
            <Stack
                gap={u(5)}
                radius="md"
                paddingBlock={u(5)}
                borderColor="subtle"
                borderWidth={1}
                bg="panel"
                paddingInline={u(2.5)}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Heading2>Activities</Heading2>
                    <Box display="inline-flex" justifyContent="flex-end" className={css.tabularNumeric}>
                        <Paragraph style={{ textAlign: 'right' }}>
                            {numberOfActivities ?? '-'} / {totalActivities ?? '-'}
                        </Paragraph>
                    </Box>
                </Box>
                {golfers}

                <Box display="flex" flexDirection="column" gap={8}>
                    <Heading4>Date range</Heading4>
                    <Box display="flex" gap={8}>
                        <Box
                            borderWidth={1}
                            borderColor="subtle"
                            bg="raised"
                            radius="md"
                            p={u(1)}
                            flexBasis="half"
                            flexGrow={1}
                            flexShrink={0}
                        >
                            <Heading4>From</Heading4>
                            <DatePicker
                                maxDate={endDate}
                                className={dateCss.date}
                                selected={startDate}
                                dateFormat="MMM d yyyy"
                                onChange={selectStartDate}
                            />
                        </Box>
                        <Box
                            borderWidth={1}
                            borderColor="subtle"
                            bg="raised"
                            radius="md"
                            p={u(1)}
                            flexBasis="half"
                            flexGrow={1}
                            flexShrink={0}
                        >
                            <Heading4>To</Heading4>
                            <DatePicker
                                minDate={startDate}
                                className={dateCss.date}
                                selected={endDate}
                                dateFormat="MMM d yyyy"
                                onChange={selectEndDate}
                            />
                        </Box>
                    </Box>
                </Box>
                <ActivityTagFilter />
                <div
                    className={css.keywordSearchContainer({ visible: !!filter.favorites })}
                    aria-hidden={!filter.favorites}
                >
                    <div className={css.keywordSearchInner}>
                        <KeywordSearch
                            value={filter.keywords ?? ''}
                            onChange={handleKeywordChange}
                            disabled={!filter.favorites}
                            placeholder="Filter by keywords"
                        />
                    </div>
                </div>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <button
                        type="button"
                        className={css.bottomButton({ active: !!filter.favorites })}
                        onClick={toggleOnlyFavorites}
                    >
                        <FolderStarIcon />
                        <Heading4>Favorites</Heading4>
                    </button>
                    <Button variant="secondary" icon="club" iconColor={colors.blue[500]} onClick={openClubModal}>
                        {selectedClubLabel ? selectedClubLabel : 'Filter by club'}
                    </Button>
                </Box>
            </Stack>
            <Box display="flex" justifyContent="center" mt={32}>
                <Button
                    variant="secondary"
                    icon="arrow-clockwise"
                    onClick={() => {
                        clearClubSelectionUi();
                        clearFilter();
                    }}
                >
                    Reset Filters
                </Button>
            </Box>
            {clubModalOpen && (
                <ClubTableModal
                    modalOpen={clubModalOpen}
                    setModalOpen={setClubModalOpen}
                    onClubClick={handleClubSelect}
                    selectedClub={filter.clubId}
                    canSelectClubGroup
                    selectedClubGroup={selectedClubGroup}
                    onClubGroupClick={handleClubGroupSelect}
                    noClubSelected={noClubSelected}
                    onNoClubClick={handleNoClubSelect}
                    clubs={clubCollections}
                />
            )}
        </Box>
    );
}
